import { useState, useEffect, useCallback } from 'react';
import { Mic, X } from 'lucide-react';
import { pushDataLayerEvent, trackButtonClick } from '../utils/tracking';
import './CoachMark.css';

export const STORAGE_KEY = 'afterlife-bot-user-journey-seen';

// 7-step coach mark walkthrough
// Steps with type:'spotlight' highlight a real UI element via SVG cutout.
// Steps with type:'center'    show a centered modal card.
const STEPS = [
  {
    id: 'welcome',
    type: 'center',
    eyebrow: 'An Action Tour Guide Experience',
    title: 'Speak with President Lincoln',
    subline: 'Gettysburg. History speaks.',
    cta: 'Get Started',
  },
  {
    id: 'start',
    type: 'spotlight',
    selector: '[aria-label="Start session"]',
    placement: 'top',
    padding: 14,
    title: 'Start the Session',
    body: 'Tap here to connect with President Lincoln. Allow microphone access when your browser asks.',
    cta: 'Got it',
  },
  {
    id: 'wake',
    type: 'center',
    icon: '🎙️',
    title: 'Say "Hey President Lincoln"',
    body: 'Once connected, say "Hey President Lincoln" or "Hello Mr. President Lincoln" — no button tapping required. He responds immediately.',
  },
  {
    id: 'ring',
    type: 'center',
    icon: '💫',
    title: 'Watch the Glow Ring',
    body: 'A blue ring pulses while President Lincoln listens. A rainbow ring animates as he speaks — a real-time voice visualizer.',
  },
  {
    id: 'interrupt',
    type: 'center',
    icon: '✋',
    title: 'Interrupt Naturally',
    body: 'Just speak mid-sentence to cut in. No tapping needed — natural conversation, like talking to a real person.',
  },
  {
    id: 'controls',
    type: 'center',
    title: 'Session Controls',
    body: 'Use Mute to silence your mic. Tap End Session when you are finished.',
    controls: true, // renders a visual preview of the two buttons
  },
  {
    id: 'help',
    type: 'spotlight',
    selector: '[aria-label="How to use this app"]',
    placement: 'bottom',
    padding: 10,
    icon: '❓',
    title: 'Need a Reminder?',
    body: 'Tap the Info button at any time to revisit this walkthrough.',
    cta: 'Get Started',
  },
];

const SPOTLIGHT_RADIUS = 14; // px — corner radius of the spotlight cutout
const CARD_WIDTH = 320;      // px — fixed card width (center + spotlight tooltips)
const WELCOME_CARD_WIDTH = 440; // px — wider hero card so headline/subline breathe
const ARROW_GAP = 8;         // px — gap between tooltip and spotlight edge

// Measures the DOM element for the current step and returns { rect, vw, vh }.
// Falls back to null if the element isn't found or has zero dimensions.
function useSpotlight(step) {
  const [data, setData] = useState(null);

  useEffect(() => {
    if (step.type !== 'spotlight' || !step.selector) {
      setData(null);
      return;
    }

    const measure = () => {
      const el = document.querySelector(step.selector);
      if (!el) { setData(null); return; }
      const r = el.getBoundingClientRect();
      if (r.width === 0 || r.height === 0) { setData(null); return; }
      setData({
        rect: { x: r.left, y: r.top, w: r.width, h: r.height },
        vw: window.innerWidth,
        vh: window.innerHeight,
      });
    };

    // rAF ensures the element has been painted before we measure
    const rafId = requestAnimationFrame(measure);
    window.addEventListener('resize', measure, { passive: true });
    return () => {
      cancelAnimationFrame(rafId);
      window.removeEventListener('resize', measure);
    };
  }, [step.type, step.selector]);

  return data;
}

function UserJourney({ onDismiss, mode = 'full' }) {
  const [stepIndex, setStepIndex] = useState(0);
  const displaySteps = mode === 'short' ? STEPS.slice(0, 1) : STEPS;
  const step = displaySteps[stepIndex];
  const isLast = stepIndex === displaySteps.length - 1;
  const isFirstSessionMode = mode === 'short';
  const hasSeen = (() => {
    try {
      return window.localStorage.getItem(STORAGE_KEY) === 'true';
    } catch (_) {
      return false;
    }
  })();
  const spotlight = useSpotlight(step);

  const finish = useCallback(() => {
    try { window.localStorage.setItem(STORAGE_KEY, 'true'); } catch (_) {}
    pushDataLayerEvent('user_journey_finished', {
      page: 'avatar',
      mode,
    });
    onDismiss?.();
  }, [mode, onDismiss]);

  const handleNext = () => {
    trackButtonClick('user_journey_next', { page: 'avatar', step_id: step.id, step_index: stepIndex + 1 });
    if (isLast) finish(); else setStepIndex((i) => i + 1);
  };
  const handleSkip = () => {
    trackButtonClick('user_journey_skip', { page: 'avatar', step_id: step.id, step_index: stepIndex + 1 });
    finish();
  };

  useEffect(() => {
    pushDataLayerEvent('user_journey_step_view', {
      page: 'avatar',
      mode,
      step_id: step.id,
      step_index: stepIndex + 1,
      total_steps: displaySteps.length,
    });
  }, [displaySteps.length, mode, step.id, stepIndex]);

  // ── Compute SVG spotlight path and card position ──────────
  const pad = step.padding || 12;
  let svgPath = null;
  let outerStyle = {};
  let showArrowDown = false;
  let showArrowUp = false;
  const isSpotlight = step.type === 'spotlight' && spotlight != null;

  if (isSpotlight) {
    const { rect, vw, vh } = spotlight;
    const rx = rect.x - pad;
    const ry = rect.y - pad;
    const rw = rect.w + pad * 2;
    const rh = rect.h + pad * 2;
    const r  = SPOTLIGHT_RADIUS;

    // evenodd path: full-screen rect minus rounded-rect hole = spotlight effect
    const outerPath = `M0,0 H${vw} V${vh} H0 Z`;
    const holePath  =
      `M${rx + r},${ry} H${rx + rw - r}` +
      ` Q${rx + rw},${ry} ${rx + rw},${ry + r}` +
      ` V${ry + rh - r}` +
      ` Q${rx + rw},${ry + rh} ${rx + rw - r},${ry + rh}` +
      ` H${rx + r}` +
      ` Q${rx},${ry + rh} ${rx},${ry + rh - r}` +
      ` V${ry + r}` +
      ` Q${rx},${ry} ${rx + r},${ry} Z`;
    svgPath = `${outerPath} ${holePath}`;

    const cardW = Math.min(CARD_WIDTH, window.innerWidth * 0.90);

    if (step.placement === 'top') {
      // Tooltip sits above the spotlight; arrow points down toward the element
      showArrowDown = true;
      outerStyle = {
        position: 'fixed',
        left: '50%',
        transform: 'translateX(-50%)',
        bottom: spotlight.vh - ry + ARROW_GAP,
        width: cardW,
        maxWidth: '90vw',
        zIndex: 10003,
      };
    } else {
      // Tooltip sits below the spotlight; arrow points up toward the element
      showArrowUp = true;
      outerStyle = {
        position: 'fixed',
        left: '50%',
        transform: 'translateX(-50%)',
        top: ry + rh + ARROW_GAP,
        width: cardW,
        maxWidth: '90vw',
        zIndex: 10003,
      };
    }
  } else {
    // Centered modal card (no spotlight) — welcome step uses full hero width
    const centerCardWidth =
      step.id === 'welcome'
        ? Math.min(WELCOME_CARD_WIDTH, window.innerWidth * 0.92)
        : CARD_WIDTH;
    outerStyle = {
      position: 'fixed',
      left: '50%',
      top: '50%',
      transform: 'translate(-50%, -50%)',
      width: centerCardWidth,
      maxWidth: step.id === 'welcome' ? '92vw' : '90vw',
      zIndex: 10003,
    };
  }

  return (
    <>
      {/* ── Overlay ── */}
      {isSpotlight && spotlight ? (
        // SVG with evenodd cutout so the target element shows through
        <svg
          aria-hidden="true"
          className="coach-overlay-svg"
          style={{ width: spotlight.vw, height: spotlight.vh }}
        >
          <path d={svgPath} fill="rgba(0,0,0,0.78)" fillRule="evenodd" />
          {/* Animated sky-blue glow border around the spotlight cutout */}
          <rect
            className="coach-spotlight-glow"
            x={spotlight.rect.x - pad}
            y={spotlight.rect.y - pad}
            width={spotlight.rect.w + pad * 2}
            height={spotlight.rect.h + pad * 2}
            rx={SPOTLIGHT_RADIUS}
            ry={SPOTLIGHT_RADIUS}
            fill="none"
            stroke="#38bdf8"
            strokeWidth="2.5"
          />
        </svg>
      ) : (
        <div className="coach-backdrop" aria-hidden="true" />
      )}

      {/* ── Coach card (remounts on each step to replay entrance animation) ── */}
      <div
        key={step.id}
        className="coach-outer"
        style={outerStyle}
        role="dialog"
        aria-modal="true"
        aria-labelledby="coach-title"
      >
        {/* Arrow pointing DOWN (tooltip above element) */}
        {showArrowDown && <div className="coach-arrow coach-arrow--down" aria-hidden="true" />}

        <div className={`coach-card ${step.id === 'welcome' ? 'coach-card--welcome' : ''}`}>
          {/* Rainbow accent strip at top */}
          <div className="coach-card__accent" aria-hidden="true" />
          {mode === 'full' && (
            <button
              type="button"
              onClick={finish}
              className="coach-card__close"
              aria-label="Close coach tour"
            >
              <X size={18} />
            </button>
          )}

          <div className="coach-card__inner">
            {step.icon && (
              <span className="coach-card__icon" aria-hidden="true">{step.icon}</span>
            )}

            {/* Controls preview — decorative only, no interaction */}
            {step.controls && (
              <div className="coach-controls-preview" aria-hidden="true">
                <div className="coach-preview-mute">
                  <Mic size={20} />
                </div>
                <div className="coach-preview-end">End Session</div>
              </div>
            )}

            {step.eyebrow && <p className="coach-card__eyebrow">{step.eyebrow}</p>}
            <h2 id="coach-title" className="coach-card__title">{step.title}</h2>
            {step.subline && <p className="coach-card__subline">{step.subline}</p>}
            {step.body && <p className="coach-card__body">{step.body}</p>}
          </div>

          {/* Progress dots */}
          {!isFirstSessionMode && (
            <div
              className="coach-dots"
              role="group"
              aria-label={`Step ${stepIndex + 1} of ${displaySteps.length}`}
            >
              {displaySteps.map((_, i) => (
                <span
                  key={i}
                  className={
                    'coach-dot' +
                    (i === stepIndex ? ' coach-dot--active' : i < stepIndex ? ' coach-dot--done' : '')
                  }
                  aria-hidden="true"
                />
              ))}
            </div>
          )}

          {/* Actions */}
          <div className="coach-actions">
            {step.id !== 'welcome' && (
              <button type="button" onClick={handleSkip} className="coach-btn-skip">
                Skip
              </button>
            )}
            <button type="button" onClick={handleNext} className={`coach-btn-next ${step.id === 'welcome' ? 'coach-btn-next--welcome' : ''}`}>
              {isLast
                ? (hasSeen ? 'Continue' : 'Get Started')
                : (mode === 'full' ? 'Next' : (step.cta || 'Next'))}
            </button>
          </div>
        </div>

        {/* Arrow pointing UP (tooltip below element) */}
        {showArrowUp && <div className="coach-arrow coach-arrow--up" aria-hidden="true" />}
      </div>
    </>
  );
}

export default UserJourney;
